import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useState } from 'react';

export interface MultiSelectCnhExpirationProps {
  isOpen?: boolean;
  size?: 'sm' | 'md';
  selectedOptions?: string[];
  onChange: (selectedOptions: string[]) => void;
}

enum CnhExpirationTypes {
  expired = 'expired',
  gracePeriod = 'gracePeriod',
  expiring = 'expiring',
  valid = 'valid',
  none = 'none',
}

const MultiSelectCnhExpiration = ({ isOpen, size = 'md', selectedOptions = [], onChange }: MultiSelectCnhExpirationProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [filter, setFilter] = useState(selectedOptions || []);

  const cnhExpirationTypesString: Record<CnhExpirationTypes, string> = {
    [CnhExpirationTypes.expired]: t('cnh_expiration.expired'),
    [CnhExpirationTypes.gracePeriod]: t('cnh_expiration.grace_period'),
    [CnhExpirationTypes.expiring]: t('cnh_expiration.expiring'),
    [CnhExpirationTypes.valid]: t('cnh_expiration.valid'),
    [CnhExpirationTypes.none]: t('cnh_expiration.none'),
  };

  const hasSameElements = selectedOptions?.length === filter?.length && selectedOptions.every((value) => filter.find((d) => d === value));

  const onClick = useCallback(() => {
    setFilter(selectedOptions || []);
  }, [selectedOptions]);

  const onChangeSelect = (typeId: string) => {
    const isSelected = filter.find((d) => d === typeId);
    const updatedSelected = isSelected ? filter.filter((d) => d !== typeId) : [...filter, typeId];
    setFilter(updatedSelected);
  };

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setFilter([]);
  }, []);

  const handleApply = useCallback(() => {
    onChange(filter);
    handleClose();
  }, [filter, handleClose, onChange]);

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  return (
    <Dropdown
      onOpenChange={onClick}
      forceOpen={isOpen}
      forceClose={forceClosed}
      portal={false}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={filter?.length === 0} onClick={() => setFilter([])}>
              {t('cnh_expiration.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightList={300}
              heightItem={28}
              data={Object.values(CnhExpirationTypes)}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item: CnhExpirationTypes) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item} key={item} checked={filter.includes(item)} onCheckedChange={() => onChangeSelect(item)} />
                  <Label htmlFor={item}>{cnhExpirationTypesString[item]}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button full variant='ghost' size='sm' onClick={handleClose}>
                {t('cnh_expiration.cancel')}
              </Button>
              <Button full variant='contained' size='sm' color='primary' onClick={handleApply} disabled={hasSameElements}>
                {t('cnh_expiration.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='calendar-clock' />}>
        {t('cnh_expiration.title')} {(selectedOptions?.length || 0) > 0 ? `(${selectedOptions?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectCnhExpiration;
